import { useCallback, useRef, useState } from 'react';
import { BOX_LIMITS, keepsAspect } from '@/slideshow/boxes';
import { snapEdges } from './sheetGrid';

/** Pixels the pointer must travel before a press becomes a drag. */
const DRAG_THRESHOLD = 4;

/** Sheet percent a box moves per arrow press; dragging steps the same. Shift-arrow is ten. */
export const BOX_STEP = 0.5;

const round = (value) => Math.round(value * 100) / 100;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Move `edges` (positions of one axis that travel together) to the nearest
 * guide, or else the lead edge onto the step. Returns the shift and the guide it met.
 */
function snapAxis(edges, guides) {
    const hit = snapEdges(edges, guides, { lead: true, grid: BOX_STEP });
    return { shift: hit.shift, guide: hit.guide ? hit.line : null };
}

/** The box after moving or resizing by (dx, dy) sheet percent. */
export function dragBox(start, mode, dx, dy, { height, guides, snap = true }) {
    const aspect = keepsAspect(start.block?.type);
    const h0 = start.h ?? height;
    let { x, y, w } = start;
    let h = start.h;
    const lines = { x: null, y: null };

    if (mode === 'move') {
        x = start.x + dx;
        y = start.y + dy;
        if (snap) {
            const sx = snapAxis([x, x + w / 2, x + w], guides.x);
            const sy = snapAxis([y, y + h0 / 2, y + h0], guides.y);
            x += sx.shift;
            y += sy.shift;
            lines.x = sx.guide;
            lines.y = sy.guide;
        }
    } else {
        const east = mode.includes('e');
        const west = mode.includes('w');
        const north = mode.includes('n');
        const south = mode.includes('s');
        if (east) w = start.w + dx;
        if (west) {
            x = start.x + dx;
            w = start.w - dx;
        }
        if (snap && (east || west)) {
            const { shift, guide } = snapAxis([east ? x + w : x], guides.x);
            if (east) w += shift;
            else {
                x += shift;
                w -= shift;
            }
            lines.x = guide;
        }
        if (w < BOX_LIMITS.minSize) {
            if (west) x -= BOX_LIMITS.minSize - w;
            w = BOX_LIMITS.minSize;
        }
        if (aspect) {
            // Vector and embed keep their shape: the width decides, the top follows a north handle.
            const ratio = start.w > 0 ? h0 / start.w : 0;
            if (north) y = start.y + (h0 - w * ratio);
        } else if (north || south) {
            let top = start.y;
            let bottom = start.y + h0;
            if (north) top += dy;
            if (south) bottom += dy;
            if (snap) {
                const { shift, guide } = snapAxis([north ? top : bottom], guides.y);
                if (north) top += shift;
                else bottom += shift;
                lines.y = guide;
            }
            if (bottom - top < BOX_LIMITS.minSize) {
                if (north) top = bottom - BOX_LIMITS.minSize;
                else bottom = top + BOX_LIMITS.minSize;
            }
            y = top;
            h = round(clamp(bottom - top, BOX_LIMITS.minSize, BOX_LIMITS.maxSize));
        }
    }

    return {
        box: {
            ...start,
            x: round(clamp(x, BOX_LIMITS.min, BOX_LIMITS.max)),
            y: round(clamp(y, BOX_LIMITS.min, BOX_LIMITS.max)),
            w: round(clamp(w, BOX_LIMITS.minSize, BOX_LIMITS.maxSize)),
            h: aspect ? null : h,
        },
        lines,
    };
}

/**
 * Drag a box over its sheet, or one of its edges and corners to resize it.
 * Below the threshold a press stays a click. Shift turns snapping off.
 *
 * @param {Object} options
 * @param {React.RefObject} options.sheetRef - the unscaled layer the box positions are percent of
 * @param {{ x: number[], y: number[] }} options.guides
 * @param {(box: Object) => void} options.onLive - while dragging
 * @param {(box: Object|null) => void} options.onSettle - on release; null when nothing moved
 */
export default function useBoxDrag({ sheetRef, guides, onLive, onSettle }) {
    const stateRef = useRef(null);
    const [lines, setLines] = useState({ x: null, y: null });

    const onPointerDown = useCallback((event, box, mode = 'move') => {
        if (event.button !== 0) return;
        const sheet = sheetRef.current;
        if (!sheet) return;
        const own = sheet.getBoundingClientRect();
        if (!own.width || !own.height) return;
        const element = event.currentTarget.closest('[data-box-id]') || event.currentTarget;
        const height = (element.getBoundingClientRect().height / own.height) * 100;
        event.stopPropagation();
        try {
            event.currentTarget.setPointerCapture?.(event.pointerId);
        } catch {
            // A pointer that is already gone can't be captured; the drag still follows it.
        }
        stateRef.current = {
            box,
            mode,
            height,
            startX: event.clientX,
            startY: event.clientY,
            width: own.width,
            sheetHeight: own.height,
            moved: false,
            last: null,
        };
    }, [sheetRef]);

    const onPointerMove = useCallback((event) => {
        const state = stateRef.current;
        if (!state) return;
        const px = event.clientX - state.startX;
        const py = event.clientY - state.startY;
        if (!state.moved && Math.hypot(px, py) < DRAG_THRESHOLD) return;
        state.moved = true;
        const result = dragBox(state.box, state.mode, (px / state.width) * 100, (py / state.sheetHeight) * 100, {
            height: state.height,
            guides,
            snap: !event.shiftKey,
        });
        state.last = result.box;
        setLines(result.lines);
        onLive?.(result.box);
    }, [guides, onLive]);

    const finish = useCallback((event, cancelled = false) => {
        const state = stateRef.current;
        if (!state) return;
        stateRef.current = null;
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        setLines({ x: null, y: null });
        onSettle?.(!cancelled && state.moved ? state.last : null);
    }, [onSettle]);

    return {
        lines,
        bind: (box, mode = 'move') => ({
            onPointerDown: (event) => onPointerDown(event, box, mode),
            onPointerMove,
            onPointerUp: (event) => finish(event),
            onPointerCancel: (event) => finish(event, true),
        }),
    };
}
